import React, { useState } from 'react';
import { Languages, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { useLang, tx } from '../i18n/LanguageContext';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * Reusable Auto-Translate button.
 *
 * Props:
 *  - sourceLang: 'ar' | 'en'
 *  - targetLang: 'ar' | 'en'
 *  - fields: Array<{ source: string, target: string }>
 *      Each entry maps a field path in `getValues()` to the target field that should
 *      receive the translation. Field paths use dot notation, e.g. "titleAr" or
 *      "features.0.ar" to support nested arrays.
 *  - getValues: () => object  (returns latest form state)
 *  - setValue: (path: string, value: string) => void
 *  - className?: string
 *  - label?: string  (optional override)
 *  - testId?: string
 */
const getByPath = (obj, path) => {
  if (!obj) return undefined;
  return path.split('.').reduce((acc, key) => {
    if (acc === undefined || acc === null) return undefined;
    if (Array.isArray(acc)) return acc[parseInt(key, 10)];
    return acc[key];
  }, obj);
};

const TranslateButton = ({
  sourceLang,
  targetLang,
  fields,
  getValues,
  setValue,
  className = '',
  label,
  testId = 'auto-translate-btn',
  size = 'sm',
}) => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const [busy, setBusy] = useState(false);

  const sourceName = sourceLang === 'ar' ? L('العربية', 'Arabic') : L('الإنجليزية', 'English');
  const targetName = targetLang === 'ar' ? L('العربية', 'Arabic') : L('الإنجليزية', 'English');
  const buttonLabel = label || L(`ترجمة تلقائية (${sourceName} → ${targetName})`, `Auto-translate (${sourceName} → ${targetName})`);

  const run = async () => {
    const values = getValues() || {};
    // Build items list — only translate non-empty source fields
    const items = fields
      .map((f, idx) => {
        const text = getByPath(values, f.source);
        return { key: `f${idx}`, text: typeof text === 'string' ? text : '', target: f.target };
      })
      .filter(it => (it.text || '').trim().length > 0);

    if (items.length === 0) {
      toast.error(L('املأ الحقول بالعربية أولاً', 'Fill in the source-language fields first'));
      return;
    }

    setBusy(true);
    try {
      const { data } = await api.post('/translate/bulk', {
        source_lang: sourceLang,
        target_lang: targetLang,
        items: items.map(it => ({ key: it.key, text: it.text })),
      });
      const results = data?.results || {};
      let applied = 0;
      items.forEach(it => {
        const translated = results[it.key];
        if (typeof translated === 'string' && translated.length > 0) {
          setValue(it.target, translated);
          applied += 1;
        }
      });
      if (applied > 0) {
        toast.success(L(`تمت ترجمة ${applied} حقل`, `Translated ${applied} field(s)`));
      } else {
        toast.error(L('لم يتم استلام ترجمة', 'No translation received'));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || L('فشلت الترجمة', 'Translation failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      onClick={run}
      disabled={busy}
      size={size}
      data-testid={testId}
      className={`bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white border-0 h-9 px-3 text-xs font-semibold ${className}`}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin me-1.5" /> : <Languages className="w-3.5 h-3.5 me-1.5" />}
      {buttonLabel}
    </Button>
  );
};

export default TranslateButton;
